/* eslint-disable no-eval */
import {
	PerspectiveCamera,
	WebGLRenderer,
	SphereGeometry,
	MeshPhongMaterial,
	TextureLoader,
	Raycaster,
	Scene,
	Vector3,
	Mesh,
	MeshBasicMaterial,
	DirectionalLight,
	AmbientLight,
} from 'three';
import OrbitControls from 'three-orbitcontrols';
import Viewport from '@/utils/viewport';
import axios from 'axios';
import { transitionEnd, DomCreate, DomAppend, DomRemove } from '@/utils/dom';
import { withPlugins, domEvents } from '@spon/plugins';
// import { eventBus } from '@spon/plugins';

const circleImage = image => {
	const imgTmpl = image
		? `
	<div class="globe-article__img-container">
		<img src="${image}" class="globe-article__img">
	</div>`
		: '';

	return imgTmpl;
};

const tmpl = data => {
	return `
		${circleImage(data.circleImage)}

		<h3 class="h3 globe-article__title">${data.title}</h3>
		<span class="globe-article__location">${data.caseStudyLocationName}</span>
		<div class="globe-article__excerpt">
			<p>${data.intro}</p>
		</div>`;
};

class GlobeClass {
	constructor(el) {
		this.$el = el;
		this.$globeEl = this.$el.querySelector('.js-globe');

		this.caseStudies = {};
		this.geolocationGeometries = [];
		this.targetRotation = 0;
		this.targetRotationOnMouseDown = 0;
		this.mouseX = 0;
		this.mouseXOnMouseDown = 0;

		this.isMouseDown = false;

		this.raycaster = new Raycaster();
		this.scene = new Scene();

		this.up = new Vector3(0, 1, 0);

		this.setupScene().init();

		this.viewport = new Viewport(true);
		this.viewport.on('change', this.viewportResize);

		this.renderer.domElement.addEventListener(
			'click',
			this.clickHandler.bind(this)
		);
	}

	// events = {
	// 	'change input[name="globeCategory"]': 'categoryHandler',
	// 	'click [data-category-select]': 'categorySelector',
	// };

	fetchCaseStudies = url => {
		return axios({
			method: 'get',
			url,
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
				'Content-Type': 'application/x-www-form-urlencoded',
				'Cache-Control': 'no-cache, no-store, must-revalidate',
				Pragma: 'no-cache',
				Expires: '0',
			},
		});
	};

	addMarkers = data => {
		// Loop through coords to and add markers to globe
		data.forEach(caseStudy => {
			this.addGeolocation(caseStudy);
		});
	};

	setupScene = () => {
		this.addCamera();

		this.addRenderer();

		this.setupWorld();

		this.addLights();

		this.addControls();

		return this;
	};

	clickHandler = e => {
		e.preventDefault();
		const mouseVectorX =
			typeof event.offsetX === 'undefined' ? event.layerX : event.offsetX;
		const mouseVectorY =
			typeof event.offsetY === 'undefined' ? event.layerY : event.offsetY;

		const vector = new Vector3();
		vector.set(
			(mouseVectorX / this.renderer.domElement.width) * 2 - 1,
			-(mouseVectorY / this.renderer.domElement.height) * 2 + 1,
			0.5
		);

		this.raycaster.setFromCamera(vector, this.camera);

		// this.raycaster.setFromCamera(this.mouse, this.camera)
		const intersects = this.raycaster.intersectObjects(
			this.geolocationGeometries
		);

		if (intersects.length !== 0) {
			this.geolocationGeometries.forEach(location => {
				location.material.color.setRGB(0.67, 0.67, 0.67);
			});

			for (let i = 0; i < intersects.length; i += 1) {
				const intersection = intersects[i];
				const obj = intersection.object;

				obj.material.color.setRGB(0.416, 0.0235, 0.698);

				this.transitionCaseStudy(obj);
			}
		}
	};

	transitionCaseStudy = ({ caseStudy }) => {
		const globeArticle = document.querySelector('.l-globe-article');
		globeArticle.style.opacity = 0;

		globeArticle.addEventListener(transitionEnd, () => {
			while (globeArticle.firstChild) {
				globeArticle.removeChild(globeArticle.firstChild);
			}

			const elWrapper = DomCreate('div', tmpl(caseStudy));
			elWrapper.classList.add('globe__article', 'globe-article');
			DomAppend(elWrapper, globeArticle);
			globeArticle.style.opacity = 1;
		});
	};

	init = () => {
		this.$globeEl.appendChild(this.renderer.domElement);

		this.animateScene();

		return this;
	};

	addCamera = () => {
		this.camera = new PerspectiveCamera(
			45,
			this.$globeEl.clientWidth / this.$globeEl.clientWidth,
			1,
			10000
		);
		this.camera.position.z = 1000;
		this.camera.lookAt(this.scene.position);
		this.scene.add(this.camera);
	};

	addRenderer = () => {
		this.renderer = new WebGLRenderer({ alpha: true });
		this.renderer.setSize(this.$globeEl.clientWidth, this.$globeEl.clientWidth);
		// this.renderer.setClearColor(0x000000, 0);
	};

	addControls = () => {
		this.controls = new OrbitControls(this.camera, this.renderer.domElement);
		this.controls.enableDamping = true;
		this.controls.dampingFactor = 1.25;
		this.controls.enablePan = false;
		this.controls.enableZoom = false;
		this.controls.autoRotate = true;
		this.controls.autoRotateSpeed = -0.3;
	};

	setupWorld = () => {
		const worldTexture = new TextureLoader().load(
			'/dist/img/earth_white-01.png',
			() => {
				// this.listener.trigger('globe:loaded');
				worldTexture.needsUpdate = true;
				worldTexture.crossOrigin = true;

				// Setup some geometries
				this.worldGeometry = new SphereGeometry(350, 50, 50);
				const worldMaterial = new MeshPhongMaterial({
					color: 0xffffff,
					specular: 0x050505,
					shininess: 100,
					map: worldTexture,
				});

				this.worldMesh = new Mesh(this.worldGeometry, worldMaterial);
				this.scene.add(this.worldMesh);

				this.caseStudies = this.fetchCaseStudies('/case-studies.json').then(
					({ data }) => {
						this.caseStudies = data.data;
						this.addMarkers(this.caseStudies);
					}
				);
			}
		);
	};

	addLights = () => {
		// Add directional ligting to camera, so it doesn't move on interaction
		const light = new DirectionalLight(0xffffff, 0.25);
		light.position.set(1, 1, 1);
		this.camera.add(light);

		const ambientLight = new AmbientLight(0xffffff, 1); // soft white light
		ambientLight.position.set(0, 0, 0);
		this.camera.add(ambientLight);
	};

	addGeolocation = data => {
		const { latitude, longitude } = data;

		const radius = 175;
		const height = 5;

		const phi = (latitude * Math.PI) / 180;
		const theta = ((longitude - 180) * Math.PI) / 180;
		const x = -(radius + height) * Math.cos(phi) * Math.cos(theta);
		const y = (radius + height) * Math.sin(phi);
		const z = (radius + height) * Math.cos(phi) * Math.sin(theta);

		const vectorBlob = new Vector3(x, y, z);

		// Create blob geometry marker
		this.blobGeometry = new SphereGeometry(
			175,
			50,
			50,
			0,
			Math.PI * 2,
			0,
			this.viewport.width < 800 ? Math.PI / 35 : Math.PI / 50
		);

		this.blobGeometry.needsUpdate = true;
		const blobMaterial = new MeshBasicMaterial({ color: 0xaaaaaa });
		this.locationGeometry = new Mesh(this.blobGeometry, blobMaterial);

		// Position geometry at coordinate
		this.locationGeometry.position.x = vectorBlob.x;
		this.locationGeometry.position.y = vectorBlob.y;
		this.locationGeometry.position.z = vectorBlob.z;
		this.locationGeometry.caseStudy = data;
		this.locationGeometry.categories = data.category;
		this.locationGeometry.lookAt(new Vector3(0, 0, 0));
		this.locationGeometry.rotateX(-1.5708);

		// Push new geometry into array for later
		this.geolocationGeometries.push(this.locationGeometry);

		// add blog to scene
		this.worldMesh.add(this.locationGeometry);
	};

	animateScene = () => {
		this.raf = requestAnimationFrame(this.animateScene);

		this.controls.update();

		this.render();
	};

	render = () => {
		this.renderer.render(this.scene, this.camera);
	};

	viewportResize = () => {
		// this.camera.aspect = this.$globeEl.clientWidth / this.$globeEl.clientHeight;
		// this.camera.updateProjectionMatrix();
		// this.renderer.setSize(
		// 	this.$globeEl.clientWidth,
		// 	this.$globeEl.clientHeight
		// );
	};

	unmount = () => {
		cancelAnimationFrame(this.raf); // Stop the animation
		this.renderer.domElement.addEventListener('click', null, false); // remove listener to render
		this.scene = null;
		this.projector = null;
		this.camera = null;
		this.controls = null;
		this.renderer = null;
		DomRemove(this.$el);

		this.viewport.destroy();
	};
}

function Globe({ node, plugins: { addEvents, removeEvents } }) {
	const globeClass = new GlobeClass(node);

	const categorySelector = (
		e,
		$el,
		$categoryPopup = document.getElementById('category-list')
	) => {
		const { visible } = $categoryPopup.dataset;

		if (visible === 'true') {
			$categoryPopup.classList.remove('-is-active');
			$categoryPopup.dataset.visible = 'false';
		} else {
			$categoryPopup.classList.add('-is-active');
			$categoryPopup.dataset.visible = 'true';
		}
	};

	const categoryHandler = (e, $el) => {
		const categoryId = $el.dataset.categoryId;

		globeClass.geolocationGeometries.forEach(location => {
			location.visible = false;
			if (location.categories.includes(categoryId) || categoryId === 'null') {
				location.visible = true;
			}
		});

		if (globeClass.viewport.width <= 740) categorySelector();
	};

	addEvents({
		'change input[name="globeCategory"]': categoryHandler,
	});

	return () => {
		removeEvents();
		globeClass.unmount();
	};
}

export default withPlugins(domEvents)(Globe);


if (module.hot) {module.hot.accept(function(err) {
if (err) {
console.error(err);
}
});
}
